import { useEffect } from "react";
import { DEFAULT_SOCIAL_IMAGE, SITE_NAME, SITE_ORIGIN } from "@/lib/publicSeo";

export { SITE_ORIGIN } from "@/lib/publicSeo";

function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    element.dataset.pageSeo = "true";
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function removeMeta(attribute, key) {
  document.head.querySelector(`meta[${attribute}="${key}"]`)?.remove();
}

export default function PageSeo({
  title,
  description,
  canonicalPath,
  type = "website",
  robots = "index, follow",
  image = DEFAULT_SOCIAL_IMAGE,
  imageAlt = SITE_NAME,
  structuredData = null,
  htmlLang = "es-ES",
  ogLocale = "es_ES",
  alternates = [],
}) {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonicalUrl = canonicalPath === null ? null : `${SITE_ORIGIN}${canonicalPath}`;
    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    document.documentElement.lang = htmlLang;
    upsertMeta("property", "og:locale", ogLocale);
    if (canonicalUrl) upsertMeta("property", "og:url", canonicalUrl); else removeMeta("property", "og:url");
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:alt", imageAlt);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
    upsertMeta("name", "twitter:image:alt", imageAlt);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonicalUrl && !canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      canonical.dataset.pageSeo = "true";
      document.head.appendChild(canonical);
    }
    if (canonicalUrl) canonical.href = canonicalUrl; else canonical?.remove();

    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => link.remove());
    alternates.forEach(({ hreflang, href }) => {
      const alternate = document.createElement("link");
      alternate.rel = "alternate";
      alternate.hreflang = hreflang;
      alternate.href = href;
      alternate.dataset.pageSeo = "true";
      document.head.appendChild(alternate);
    });

    const oldStructuredData = document.getElementById("page-structured-data");
    if (oldStructuredData) oldStructuredData.remove();
    if (structuredData) {
      const script = document.createElement("script");
      script.id = "page-structured-data";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    return () => {
      document.getElementById("page-structured-data")?.remove();
    };
  }, [alternates, canonicalPath, description, htmlLang, image, imageAlt, ogLocale, robots, structuredData, title, type]);

  return null;
}
